<template>
  <div
    :class="{ 'poster-print': posterMode }"
    class="annual-calendar">
    <div class="annual-calendar__grid">
      <div
        v-for="month in months"
        :key="month.value"
        class="annual-calendar__month"
      >
        <h2>{{ month.name }} {{ month.year }}</h2>

        <!-- Render asd20-calendar -->
        <asd20-calendar
          :events="getEventsForMonth(month.value, month.year)"
          :month="month.value"
          :year="month.year"
          :absolute="false"
          :short-weekdays="true"
          :annual-view="true"
        />

        <!-- List of events beneath each month -->
        <ul class="monthly-events">
          <li
            v-for="event in getUniqueDistrictEventsForMonth(
              month.value,
              month.year
            )"
            :key="event.uid"
            :style="{ '--event-color': event.calendarColor || '#000' }"
            class="event-item"
          >
            <span>
              <span class="date-range">{{ displayDateRange(event) }}</span
              ><span>: {{ event.summary }} </span>
            </span>
          </li>
        </ul>
      </div>
    </div>
    <div class="calendar-key-and-date">
      <div class="calendar-key">
        <div
          v-for="(keyItem, index) in computedCalendarKey"
          :key="index"
          class="calendar-key__item"
        >
          <span
            :style="{ backgroundColor: keyItem.color }"
            class="calendar-key__dot"
          />
          <span class="calendar-key__label">{{ keyItem.label }}</span>
        </div>
      </div>
      <div class="address-footer">
        <span v-if="selectedOrganization">
          {{ selectedOrganization.address.street1 }} | {{ selectedOrganization.address.city }},
          {{ selectedOrganization.address.state }} {{ selectedOrganization.address.zip }} | Phone:
          {{ selectedOrganization.phone }} | Web: {{ selectedOrganization.website.replace(/^https?:\/\//, '') }}
        </span>
        <span>Print Date: {{ currentDate }}</span>
      </div>
    </div>
</div></template>

<script>
import Asd20Calendar from '@asd20/ui/src/components/organisms/Asd20Calendar'
import Asd20CalendarEventButton from '@asd20/ui/src/components/atoms/Asd20CalendarEventButton'
import expandEvents from '@asd20/ui/src/helpers/expandEvents'
import calendarKeyConfigs from '../../helpers/calendarKeyConfigs'

export default {
  name: 'Asd20AnnualCalendar',

  components: {
    Asd20Calendar,
    Asd20CalendarEventButton,
  },

  props: {
    events: { type: Array, required: true, default: () => [] },
    startYear: { type: Number, default: new Date().getFullYear() },
    posterMode: { type: Boolean, default: false },
    selectedOrganization: { type: Object, default: () => {} },
  },

  data() {
    return {
      monthNames: [
        'January',
        'February',
        'March',
        'April',
        'May',
        'June',
        'July',
        'August',
        'September',
        'October',
        'November',
        'December',
      ],
    }
  },

  computed: {
    months() {
      const months = []
      const startMonth = 6 // July (zero-indexed)
      let year = this.startYear

      for (let i = 0; i < 12; i++) {
        const monthValue = (startMonth + i) % 12
        if (monthValue === 0 && i !== 0) year++
        months.push({
          value: monthValue,
          name: this.monthNames[monthValue],
          year,
        })
      }

      return months
    },

    expandedEvents() {
      // Include past events in the annual calendar
      return expandEvents(this.events, true)
    },

    computedCalendarKey() {
      const org = this.selectedOrganization
      if (!org || !org.id) return calendarKeyConfigs.district

      if (
        this.selectedOrganization.id.includes(
          'bb6e9cd6-da25-9e6f-5e8d-072b9a7df4f0'
        )
      ) {
        return calendarKeyConfigs.aahs
      }
      if (
        this.selectedOrganization.id.includes(
          'f4656914-9ec6-1811-aa5d-31f532f483d5'
        )
      ) {
        return calendarKeyConfigs.dcchs
      }
      if (
        this.selectedOrganization.id.includes(
          'a6f3a682-acf0-909b-96d5-e840e6a44eb5'
        )
      ) {
        return calendarKeyConfigs.lhs
      }
      if (
        this.selectedOrganization.id.includes(
          '3ace095e-c792-6fa8-3299-a0db26db81dd'
        )
      ) {
        return calendarKeyConfigs.pchs
      }
      if (
        this.selectedOrganization.id.includes(
          '2a47254c-89ad-ce5e-156b-e44bec9452d1'
        )
      ) {
        return calendarKeyConfigs.rhs
      }
      if (
        this.selectedOrganization.id.includes(
          'e0f7c870-1f4d-34e2-934c-28b991f697ae'
        )
      ) {
        return calendarKeyConfigs.vhs
      }
      return calendarKeyConfigs.district
    },
    isDistrictCalendar() {
      return (
        this.organizationIds &&
        this.organizationIds.includes('26eaf390-d8ab-11e9-a3a8-5de5bba4f125')
      )
    },
    currentDate() {
      const options = { year: 'numeric', month: 'long', day: 'numeric' }
      return new Date().toLocaleDateString(undefined, options)
    },
  },

  methods: {
    // For the calendar grid - show every occurrence, including multi-day spans
    getEventsForMonth(month, year) {
      return this.expandedEvents.filter(event => {
        const eventDate = new Date(event.start)
        return (
          eventDate.getMonth() === month && eventDate.getFullYear() === year
        )
      })
    },

    // For the event list - show one item per event (even multi-day)
    getUniqueEventsForMonth(month, year) {
      const uniqueEvents = []
      const seenUids = new Set()

      this.expandedEvents.forEach(event => {
        const eventStart = new Date(event.originalStart || event.start)
        const eventEnd = new Date(event.end)

        // Adjust for all-day/multi-day logic (as you already do elsewhere)
        if ((event.allDay || event.multiDay) && eventEnd) {
          eventEnd.setDate(eventEnd.getDate() - 1)
        }

        // Check if the event spans this month
        const spansThisMonth =
          (eventStart.getFullYear() === year &&
            eventStart.getMonth() === month) ||
          (eventEnd.getFullYear() === year && eventEnd.getMonth() === month) ||
          (eventStart < new Date(year, month + 1, 1) &&
            eventEnd >= new Date(year, month, 1))

        if (spansThisMonth) {
          const key = `${event.uid}-${month}-${year}`
          if (!seenUids.has(key)) {
            uniqueEvents.push(event)
            seenUids.add(key)
          }
        }
      })

      return uniqueEvents
    },
    getUniqueDistrictEventsForMonth(month, year) {
      const uniqueEvents = []
      const seenUids = new Set()
      const DISTRICT_ORG_ID = '26eaf390-d8ab-11e9-a3a8-5de5bba4f125'

      this.expandedEvents.forEach(event => {
        if (event.calendarOrganizationId !== DISTRICT_ORG_ID) return

        const eventStart = new Date(event.originalStart || event.start)
        const eventEnd = new Date(event.end)

        if ((event.allDay || event.multiDay) && eventEnd) {
          eventEnd.setDate(eventEnd.getDate() - 1)
        }

        const spansThisMonth =
          (eventStart.getFullYear() === year &&
            eventStart.getMonth() === month) ||
          (eventEnd.getFullYear() === year && eventEnd.getMonth() === month) ||
          (eventStart < new Date(year, month + 1, 1) &&
            eventEnd >= new Date(year, month, 1))

        if (spansThisMonth) {
          const key = `${event.uid}-${month}-${year}`
          if (!seenUids.has(key)) {
            uniqueEvents.push(event)
            seenUids.add(key)
          }
        }
      })

      return uniqueEvents
    },

    formatDate(date, opts = { month: 'short', day: 'numeric' }) {
      if (!date) return ''
      return new Date(date).toLocaleDateString(undefined, opts)
    },

    displayDateRange(event) {
      if (!event.start) return ''

      const startDate = new Date(event.originalStart || event.start)
      let endDate = event.end ? new Date(event.end) : null

      // Subtract 1 day from endDate if:
      // - The event is allDay
      // - OR it is multiDay *and* an allDay event (safer logic)
      if ((event.allDay || event.multiDay) && endDate) {
        endDate.setDate(endDate.getDate() - 1)
      }

      const isSameDay =
        !endDate || startDate.toDateString() === endDate.toDateString()

      if (isSameDay) {
        return this.formatDate(startDate)
      }

      // Now display ranges
      const sameMonth =
        startDate.getMonth() === endDate.getMonth() &&
        startDate.getFullYear() === endDate.getFullYear()

      if (sameMonth) {
        const startFormatted = this.formatDate(startDate)
        const endDay = endDate.getDate()
        return `${startFormatted} - ${endDay}`
      }

      const startFormatted = this.formatDate(startDate)
      const endFormatted = this.formatDate(endDate)
      return `${startFormatted} - ${endFormatted}`
    },
  },
}
</script>

<style lang="scss">
.annual-calendar {
  padding: 1rem;
}

.annual-calendar__controls {
  display: flex;
  gap: 1rem;
  margin-bottom: 1rem;
}

.annual-calendar__grid {
  display: grid;
  grid-template-columns: repeat(
    auto-fill,
    minmax(300px, 1fr)
  ); /* 3 or 4 per row */
  gap: 1.5rem;
}

.annual-calendar__month {
  background: var(--website-page__alternate-background-t70);
  padding: 1rem;
  border: 1px solid var(--color__tertiary);
}

.monthly-events {
  font-size: 0.875rem;
  margin-top: 1rem;
  list-style: none; /* Remove default bullets */
  padding-left: 1.5rem; /* Indent space for the custom bullets */
  .date-range {
    font-weight: bold;
  }
}

.monthly-events li.event-item {
  position: relative;
  margin-bottom: 0.5rem; /* Space between events */
  padding-left: 0.25rem; /* Optional: space between bullet and text */
}

.monthly-events li.event-item::before {
  content: ''; /* Empty content to create the dot */
  position: absolute;
  left: -1.25rem; /* Positions the dot to the left of the text */
  top: 50%; /* Center vertically */
  transform: translateY(-50%); /* Perfect vertical centering */
  width: 15px; /* Dot size */
  height: 15px; /* Dot size */
  background-color: var(
    --event-color,
    #000
  ); /* Default color if var isn't set */
  border-radius: 50%; /* Makes it a circle */
}
.calendar-key {
  display: flex;
  flex-direction: column;
  margin-top: 1rem;
  gap: 1.25rem;
  font-size: 0.875rem;
  justify-content: flex-start;
  &__item {
    display: flex;
    flex-direction: row;
    align-items: center;
    margin-bottom: 0.25rem;
  }

  &__dot {
    min-width: 15px;
    height: 15px;
    border-radius: 50%;
    display: inline-block;
    margin-right: 0.5rem;
  }

  &__label {
    line-height: 1;
  }
}
.address-footer {
  display: flex;
  margin-top: 1rem;
  font-size: 1rem;
  justify-content: space-between;
}
@media (min-width: 1024px) {

  .annual-calendar {
    .calendar-key {
      flex-direction: row !important;
    }
  }
}

</style>
